<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading   = ref(false)
const saving    = ref(false)
const years     = ref([])
const showModal = ref(false)
const formError = ref('')
const form      = ref({ starts_at: '', ends_at: '' })

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get('/accounting/fiscal-years')
    years.value = res.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(load)

const openCreate = () => {
  form.value = { starts_at: '', ends_at: '' }
  formError.value = ''
  showModal.value = true
}

const save = async () => {
  if (!form.value.starts_at || !form.value.ends_at) {
    formError.value = 'Start and end dates are required.'
    return
  }
  try {
    saving.value = true
    formError.value = ''
    await axios.post('/accounting/fiscal-years', form.value)
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to create fiscal year'
  } finally { saving.value = false }
}

const close = async (id) => {
  if (!confirm('Close this fiscal year? This action cannot be undone.')) return
  try {
    await axios.post(`/accounting/fiscal-years/${id}/close`)
    load()
  } catch (e) {
    alert(e.response?.data?.message ?? 'Failed to close fiscal year')
  }
}

const statusClass = (s) => s === 'open' ? 'badge--green' : 'badge--grey'
</script>

<template>
  <div>
    <Header />
    <div class="fy-page">
      <div class="fy-toolbar">
        <h2 class="fy-title">Fiscal Years</h2>
        <button class="btn-primary" @click="openCreate">+ New Fiscal Year</button>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Closed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="5" class="tbl-loading">Loading…</td></tr>
              <tr v-else-if="!years.length"><td colspan="5" class="tbl-empty">No fiscal years configured</td></tr>
              <tr v-for="fy in years" :key="fy.id" class="data-row">
                <td>{{ fy.starts_at }}</td>
                <td>{{ fy.ends_at }}</td>
                <td><span :class="['badge', statusClass(fy.status)]">{{ fy.status }}</span></td>
                <td>{{ fy.closed_at ?? '—' }}</td>
                <td>
                  <button v-if="fy.status === 'open'" class="btn-link btn-link--red" @click="close(fy.id)">Close Year</button>
                  <span v-else class="closed-label">Closed</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>New Fiscal Year</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Start Date *</label>
              <input type="date" v-model="form.starts_at" />
            </div>
            <div class="form-group">
              <label>End Date *</label>
              <input type="date" v-model="form.ends_at" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Create' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fy-page    { padding: 0 24px 40px; }
.fy-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.fy-title   { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.table-card  { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.table-scroll { overflow-x: auto; }
.data-table  { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.data-row:hover td { background: #fafbff; }
.tbl-loading, .tbl-empty { text-align: center; color: #999; padding: 40px; }
.badge         { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--grey   { background: #f0f0f0; color: #666; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.closed-label { font-size: 13px; color: #999; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 500px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
